// ---------------------------------------------------------------------------
// DSS-34: CSS ID + Classes — custom element ID and class name inputs
// ---------------------------------------------------------------------------
import { useCallback, useId, useMemo, useState } from "react";
import { Hash, X, Tag } from "lucide-react";

// ── Types ──
interface CssIdClassesConfig {
  readonly elementId: string;
  readonly classNames: readonly string[];
}

// ── Constants ──
const ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const CLASS_PATTERN = /^[a-zA-Z_-][a-zA-Z0-9_-]*$/;
const MAX_ID_LENGTH = 64;
const MAX_CLASS_LENGTH = 64;
const MAX_CLASSES = 50;

const COMMON_UTILITY_CLASSES: readonly string[] = [
  "container",
  "mx-auto",
  "text-center",
  "text-left",
  "text-right",
  "hidden",
  "block",
  "flex",
  "grid",
  "relative",
  "absolute",
  "sticky",
  "overflow-hidden",
  "rounded",
  "rounded-lg",
  "rounded-full",
  "shadow",
  "shadow-lg",
  "sr-only",
  "animate-fade-in",
  "animate-slide-up",
  "full-width",
  "narrow",
  "wide",
];

const DEFAULT_CONFIG: CssIdClassesConfig = {
  elementId: "",
  classNames: [],
};

// ── Validators ──
function isValidId(id: string): boolean {
  if (!id) return true; // Empty is valid (optional)
  if (id.length > MAX_ID_LENGTH) return false;
  return ID_PATTERN.test(id);
}

function isValidClassName(name: string): boolean {
  if (!name) return false;
  if (name.length > MAX_CLASS_LENGTH) return false;
  return CLASS_PATTERN.test(name);
}

// ── Props ──
interface CssIdClassesProps {
  readonly value: CssIdClassesConfig | undefined;
  readonly onChange: (config: CssIdClassesConfig) => void;
}

// ── Component ──
export default function CssIdClasses({ value, onChange }: CssIdClassesProps) {
  const config = value ?? DEFAULT_CONFIG;
  const baseId = useId();
  const [classInput, setClassInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const idError = useMemo(() => {
    if (!config.elementId) return null;
    if (!isValidId(config.elementId)) {
      return "Must start with a letter, no spaces";
    }
    return null;
  }, [config.elementId]);

  const handleIdChange = useCallback(
    (id: string) => {
      const trimmed = id.trim().slice(0, MAX_ID_LENGTH);
      onChange({ ...config, elementId: trimmed });
    },
    [config, onChange],
  );

  const addClass = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      if (!isValidClassName(trimmed)) return;
      if (config.classNames.includes(trimmed)) return;
      if (config.classNames.length >= MAX_CLASSES) return;

      onChange({
        ...config,
        classNames: [...config.classNames, trimmed],
      });
      setClassInput("");
      setShowSuggestions(false);
    },
    [config, onChange],
  );

  const removeClass = useCallback(
    (name: string) => {
      onChange({
        ...config,
        classNames: config.classNames.filter((c) => c !== name),
      });
    },
    [config, onChange],
  );

  const handleClassInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        addClass(classInput);
      }
      if (e.key === "Backspace" && !classInput && config.classNames.length > 0) {
        const last = config.classNames[config.classNames.length - 1];
        if (last) removeClass(last);
      }
    },
    [classInput, addClass, removeClass, config.classNames],
  );

  // Autocomplete filtering
  const suggestions = useMemo(() => {
    if (!classInput.trim()) return [];
    const query = classInput.toLowerCase();
    return COMMON_UTILITY_CLASSES.filter(
      (cls) => cls.toLowerCase().includes(query) && !config.classNames.includes(cls),
    ).slice(0, 8);
  }, [classInput, config.classNames]);

  // Selector preview
  const selectorPreview = useMemo(() => {
    const parts: string[] = [];
    if (config.elementId) parts.push(`#${config.elementId}`);
    for (const cls of config.classNames) {
      parts.push(`.${cls}`);
    }
    return parts.join("") || "(no selector)";
  }, [config.elementId, config.classNames]);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Hash className="h-3.5 w-3.5" />
        ID + Classes
      </div>

      {/* ID input */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${baseId}-id`}
          className="text-[10px] font-medium text-[var(--color-muted-foreground)]"
        >
          Element ID
        </label>
        <input
          id={`${baseId}-id`}
          type="text"
          value={config.elementId}
          onChange={(e) => handleIdChange(e.target.value)}
          placeholder="my-section"
          maxLength={MAX_ID_LENGTH}
          className={[
            "h-7 rounded border bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]",
            idError
              ? "border-[var(--color-destructive)]"
              : "border-[var(--color-border)]",
          ].join(" ")}
          aria-label="Element ID"
          aria-describedby={idError ? `${baseId}-id-error` : undefined}
        />
        {idError && (
          <span
            id={`${baseId}-id-error`}
            className="text-[10px] text-[var(--color-destructive)]"
            role="alert"
          >
            {idError}
          </span>
        )}
      </div>

      {/* Classes input */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${baseId}-class`}
          className="text-[10px] font-medium text-[var(--color-muted-foreground)]"
        >
          CSS Classes
        </label>

        {/* Class pills */}
        {config.classNames.length > 0 && (
          <div className="flex flex-wrap gap-1" role="list" aria-label="Active CSS classes">
            {config.classNames.map((cls) => (
              <span
                key={cls}
                role="listitem"
                className="flex items-center gap-1 rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-primary)]"
              >
                <Tag className="h-2.5 w-2.5" />
                {cls}
                <button
                  type="button"
                  onClick={() => removeClass(cls)}
                  className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-[var(--color-destructive)]/20 hover:text-[var(--color-destructive)]"
                  aria-label={`Remove class ${cls}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Input with autocomplete */}
        <div className="relative">
          <input
            id={`${baseId}-class`}
            type="text"
            value={classInput}
            onChange={(e) => {
              setClassInput(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={handleClassInputKeyDown}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => {
              // Delay to allow click on suggestion
              setTimeout(() => setShowSuggestions(false), 150);
            }}
            placeholder="Type class name, press Enter"
            className="h-7 w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
            aria-label="Add CSS class"
            aria-expanded={showSuggestions && suggestions.length > 0}
            aria-autocomplete="list"
          />

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              className="absolute left-0 top-full z-10 mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] py-1 shadow-lg"
              role="listbox"
              aria-label="Class suggestions"
            >
              {suggestions.map((cls) => (
                <button
                  key={cls}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addClass(cls)}
                  className="flex w-full items-center gap-1.5 px-2.5 py-1 text-left text-[11px] text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-muted)]"
                >
                  <Tag className="h-3 w-3 text-[var(--color-muted-foreground)]" />
                  {cls}
                </button>
              ))}
            </div>
          )}
        </div>

        {config.classNames.length >= MAX_CLASSES && (
          <span className="text-[10px] text-[var(--color-muted-foreground)]">
            Maximum {MAX_CLASSES} classes reached
          </span>
        )}
      </div>

      {/* Selector preview */}
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">
          Selector preview
        </span>
        <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
          <code className="text-[10px] text-[var(--color-muted-foreground)]">
            {selectorPreview}
          </code>
        </div>
      </div>
    </div>
  );
}
